import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {Box, Divider, Drawer, Grid, IconButton, Stack, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {getHomeInternalUrl} from "../../constants/appRoutes";
import {NodeVisibility} from "../../model/IHubMinimalNode.d.ts";
import {I18N_ADORNMENT_VIEW_MODE_ICON} from "../i18n-input-adornment-select";
import A11ySelectButton from "./components/A11ySelectButton";
import AppLanguageSelectButton from "./components/AppLanguageSelectButton";
import DashboardButton from "./components/DashboardButton";
import HubLogo from "./components/HubLogo";
import HubName from "./components/HubName";
import InfoButton from "./components/InfoButton";
import NavigationDrawerButtons from "./components/NavigationDrawerButtons";
import NodeHomeButton from "./components/NodeHomeButton";
import NodeSelector from "./components/NodeSelector";
import SearchButton from "./components/SearchButton";
import SettingsSelectButton from "./components/SettingsSelectButton";
import TerritoryToolsButton from "./components/TerritoryToolsButton";
import ToolsSelectButton from "./components/ToolsSelectButton";
import UserItemsSelectButton from "./components/UserItemsSettingsButton";
import UserSelectButton from "./components/UserSelectButton";
import {localizeI18nObj} from "../../utils/i18n";
import {areElementsOverlap} from "../../utils/other";
import {HEADER_BUTTON_VIEW_MODE_ICON} from "./utils";

const firstRowContainerStyle = {
  position: "absolute",
  padding: "0 8px"
};

const firstRowLeftContainerStyle = {
  left: "0px"
};

const firstRowCenterContainerStyle = {
  cursor: "pointer"
};

const firstRowRightContainerStyle = {
  right: "0px"
};

const hiddenContainerStyle = {
  visibility: "hidden"
};

const HeaderDefault = ({
  baseURL,
  isA11y,
  defaultLanguage,
  languages,
  moduleConfigs,
  hub,
  nodes,
  node,
  title = node?.name,
  noNode = !node?.nodeId,
  catalog,
  user,
  filteredCatalog,
  emptyHeader,
  isDefaultAndUniqueNode,
  selectedCategoryPath,
  selectedDataset,
  onRender
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [isToolbarDrawerOpen, setisToolbarDrawerOpen] = useState(false);
  const [isFirstRowCollapsed, setisFirstRowCollapsed] = useState(false);
  const [isCenterContainerCollapsed, setisCenterContainerCollapsed] = useState(false);

  const headerLogoSmallURL = useMemo(() => hub.extras.HeaderLogoSmallURL || "", [hub.extras]);
  const headerLogoAlt = useMemo(
    () => localizeI18nObj(hub.extras.HeaderLogoAlt, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );
  const headerLogoHref = useMemo(() => hub.extras.HeaderLogoHref || "", [hub.extras]);
  const headerLogoTitle = useMemo(
    () => localizeI18nObj(hub.extras.headerLogoTitle, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );

  const handleDefaultHeaderStyle = useCallback(() => {
    const newIsFirstRowCollapsed =
      areElementsOverlap("header__first-row__center-container", "header__first-row__left-container") ||
      areElementsOverlap("header__first-row__center-container", "header__first-row__right-container");

    const newIsCenterContainerCollapsed =
      areElementsOverlap("header__first-row__center-container", "header__first-row__left-container--collapsed") ||
      areElementsOverlap("header__first-row__center-container", "header__first-row__right-container--collapsed");

    setisFirstRowCollapsed(newIsFirstRowCollapsed);
    setisCenterContainerCollapsed(newIsCenterContainerCollapsed);

    if (onRender) {
      setTimeout(() => onRender(), 0);
    }
  }, [onRender]);

  useEffect(() => {
    window.addEventListener("resize", handleDefaultHeaderStyle);
    return () => {
      window.removeEventListener("resize", handleDefaultHeaderStyle);
    };
  }, [handleDefaultHeaderStyle]);

  useEffect(() => {
    handleDefaultHeaderStyle();
  });

  const onToolbarDrawerOpen = useCallback(() => {
    setisToolbarDrawerOpen(true);
  }, []);

  const onToolbarDrawerClose = useCallback(() => {
    setisToolbarDrawerOpen(false);
  }, []);

  const isSecondRowVisible = isCenterContainerCollapsed;
  const isThirdRowVisible = isFirstRowCollapsed && !noNode && !isDefaultAndUniqueNode;

  return emptyHeader ? (
    <Grid container columnSpacing={1} alignItems="center" wrap="nowrap" justifyContent="center">
      <HubLogo
        hub={hub}
        headerLogoTitle={headerLogoTitle}
        headerLogoHref={headerLogoHref}
        baseURL={baseURL}
        headerLogoAlt={headerLogoAlt}
        handleStyle={handleDefaultHeaderStyle}
        headerLogoSmallURL={headerLogoSmallURL}
      />
      <HubName hub={hub} />
    </Grid>
  ) : (
    <Fragment>
      <Box
        id="header__first-row"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "64px"
        }}
      >
        <Box
          id="header__first-row__left-container"
          sx={[firstRowContainerStyle, firstRowLeftContainerStyle, isFirstRowCollapsed && hiddenContainerStyle]}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Fragment>
              {node?.visible !== NodeVisibility.No ? (
                !noNode ? (
                  <Fragment>
                    {catalog && !catalog.isEmpty && (
                      <NavigationDrawerButtons
                        selectedCategoryPath={selectedCategoryPath}
                        selectedDataset={selectedDataset}
                        title={title}
                        nodes={nodes}
                        catalog={catalog}
                        node={node}
                        hub={hub}
                        filteredCatalog={filteredCatalog}
                        defaultLanguage={defaultLanguage}
                        languages={languages}
                        moduleConfigs={moduleConfigs}
                      />
                    )}
                    <NodeHomeButton />
                    <NodeSelector isDefaultUniqueNode={isDefaultAndUniqueNode} node={node} nodes={nodes} />
                  </Fragment>
                ) : (
                  <span />
                )
              ) : (
                <Grid className="home-btn">
                  <Tooltip title={t("components.header.actions.home.title")}>
                    <IconButton
                      color="inherit"
                      onClick={() => navigate(getHomeInternalUrl())}
                      aria-label={t("components.header.actions.home.ariaLabel")}
                    >
                      <HomeIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <TerritoryToolsButton />
            </Fragment>
          </Grid>
        </Box>
        <Box
          id="header__first-row__left-container--collapsed"
          sx={[firstRowContainerStyle, firstRowLeftContainerStyle, !isFirstRowCollapsed && hiddenContainerStyle]}
        >
          {!noNode && node?.visible !== NodeVisibility.No && catalog && !catalog.isEmpty && (
            <Grid container spacing={1} alignItems="center" wrap="nowrap">
              <NavigationDrawerButtons
                selectedCategoryPath={selectedCategoryPath}
                selectedDataset={selectedDataset}
                title={title}
                nodes={nodes}
                catalog={catalog}
                node={node}
                hub={hub}
                filteredCatalog={filteredCatalog}
                defaultLanguage={defaultLanguage}
                languages={languages}
                moduleConfigs={moduleConfigs}
              />
            </Grid>
          )}
        </Box>
        <Box
          id="header__first-row__center-container"
          sx={[
            firstRowContainerStyle,
            firstRowCenterContainerStyle,
            isCenterContainerCollapsed && hiddenContainerStyle
          ]}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <HubLogo
              hub={hub}
              headerLogoTitle={headerLogoTitle}
              headerLogoHref={headerLogoHref}
              baseURL={baseURL}
              headerLogoAlt={headerLogoAlt}
              handleStyle={handleDefaultHeaderStyle}
              headerLogoSmallURL={headerLogoSmallURL}
            />
            <HubName hub={hub} />
          </Grid>
        </Box>
        <Box
          id="header__first-row__center-container--collapsed"
          sx={[
            firstRowContainerStyle,
            firstRowCenterContainerStyle,
            !isCenterContainerCollapsed && hiddenContainerStyle
          ]}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <HubLogo
              hub={hub}
              headerLogoTitle={headerLogoTitle}
              headerLogoHref={headerLogoHref}
              baseURL={baseURL}
              headerLogoAlt={headerLogoAlt}
              handleStyle={handleDefaultHeaderStyle}
              headerLogoSmallURL={headerLogoSmallURL}
              isCollapsed
            />
          </Grid>
        </Box>
        <Box
          id="header__first-row__right-container"
          sx={[firstRowContainerStyle, firstRowRightContainerStyle, isFirstRowCollapsed && hiddenContainerStyle]}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <SearchButton noNode={noNode} catalog={catalog} />
            <AppLanguageSelectButton viewMode={I18N_ADORNMENT_VIEW_MODE_ICON} />
            <A11ySelectButton viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <DashboardButton moduleConfigs={moduleConfigs} node={node} hub={hub} />
            <SettingsSelectButton user={user} nodes={nodes} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <UserSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <UserItemsSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <ToolsSelectButton node={node} user={user} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <InfoButton hub={hub} />
          </Grid>
        </Box>
        <Box
          id="header__first-row__right-container--collapsed"
          sx={[firstRowContainerStyle, firstRowRightContainerStyle, !isFirstRowCollapsed && hiddenContainerStyle]}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid className="toolbar-drawer-btn">
              <Tooltip title={t("components.header.actions.openToolbarDrawer.title")}>
                <IconButton
                  color="inherit"
                  onClick={onToolbarDrawerOpen}
                  aria-label={t("components.header.actions.openToolbarDrawer.ariaLabel")}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {isSecondRowVisible && (
        <Box
          id="header__second-row"
          sx={{
            display: "flex",
            justifyContent: "center",
            "& $hubName": {
              height: "unset",
              padding: "0px",
              fontSize: "24px"
            }
          }}
          style={{paddingBottom: isThirdRowVisible ? 0 : 8}}
        >
          <HubName hub={hub} />
        </Box>
      )}
      {isThirdRowVisible && (
        <Box id="header__third-row" sx={{paddingLeft: "12px"}}>
          <NodeSelector isDefaultUniqueNode={isDefaultAndUniqueNode} node={node} nodes={nodes} />
        </Box>
      )}
      <Drawer
        anchor="right"
        open={isToolbarDrawerOpen}
        PaperProps={{
          sx: {
            overflow: "hidden",
            padding: "8px"
          }
        }}
        onClose={onToolbarDrawerClose}
      >
        <nav id="toolbar-drawer" aria-label={t("components.header.toolbarDrawer.ariaLabel")}>
          <Stack spacing={1}>
            <Grid>
              <Tooltip title={t("components.header.actions.closeToolbarDrawer.title")}>
                <IconButton
                  color="inherit"
                  onClick={onToolbarDrawerClose}
                  aria-label={t("components.header.actions.closeToolbarDrawer.ariaLabel")}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid
              sx={{
                width: "100%",
                margin: "4px 0"
              }}
            >
              <Divider />
            </Grid>
            <SearchButton noNode={noNode} catalog={catalog} />
            <AppLanguageSelectButton viewMode={I18N_ADORNMENT_VIEW_MODE_ICON} />
            <A11ySelectButton viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <DashboardButton moduleConfigs={moduleConfigs} isA11y={isA11y} noNode={noNode} node={node} />
            <SettingsSelectButton user={user} nodes={nodes} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <UserSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={!isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <UserItemsSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <ToolsSelectButton node={node} user={user} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <TerritoryToolsButton />
            <InfoButton hub={hub} />
          </Stack>
        </nav>
      </Drawer>
    </Fragment>
  );
};

export default HeaderDefault;
